import { Row, Col } from 'react-bootstrap';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { Underline } from '@tiptap/extension-underline';
import { Markdown } from 'tiptap-markdown';
import { useEffect, useCallback } from 'react';

import './Documentation.scss';

const MenuBar = ({ editor }) => {
  if (!editor) return null;

  return (
    <div className="tiptap-toolbar">
      <button type="button" onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo">
        ↩
      </button>
      <button type="button" onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo">
        ↪
      </button>
      <span className="tiptap-toolbar-divider" />
      <select
        value={
          editor.isActive('heading', { level: 1 }) ? '1' : editor.isActive('heading', { level: 2 }) ? '2' : editor.isActive('heading', { level: 3 }) ? '3' : 'p'
        }
        onChange={(e) => {
          const val = e.target.value;
          if (val === 'p') {
            editor.chain().focus().setParagraph().run();
          } else {
            editor
              .chain()
              .focus()
              .toggleHeading({ level: parseInt(val) })
              .run();
          }
        }}
      >
        <option value="p">Paragraph</option>
        <option value="1">Heading 1</option>
        <option value="2">Heading 2</option>
        <option value="3">Heading 3</option>
      </select>
      <span className="tiptap-toolbar-divider" />
      <button type="button" onClick={() => editor.chain().focus().toggleBold().run()} className={editor.isActive('bold') ? 'is-active' : ''} title="Bold">
        <strong>B</strong>
      </button>
      <button type="button" onClick={() => editor.chain().focus().toggleItalic().run()} className={editor.isActive('italic') ? 'is-active' : ''} title="Italic">
        <em>I</em>
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        className={editor.isActive('underline') ? 'is-active' : ''}
        title="Underline"
      >
        <u>U</u>
      </button>
      <span className="tiptap-toolbar-divider" />
      <button
        type="button"
        onClick={() => {
          const url = window.prompt('URL');
          if (url) editor.chain().focus().setLink({ href: url }).run();
        }}
        className={editor.isActive('link') ? 'is-active' : ''}
        title="Link"
      >
        🔗
      </button>
      <span className="tiptap-toolbar-divider" />
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={editor.isActive('bulletList') ? 'is-active' : ''}
        title="Bullet list"
      >
        •≡
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className={editor.isActive('orderedList') ? 'is-active' : ''}
        title="Ordered list"
      >
        1≡
      </button>
      <span className="tiptap-toolbar-divider" />
      <button type="button" onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()} title="Insert table">
        ⊞
      </button>
      <span className="tiptap-toolbar-divider" />
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
        className={editor.isActive('codeBlock') ? 'is-active' : ''}
        title="Code block"
      >
        {'<>'}
      </button>
    </div>
  );
};

const Documentation = ({ state, setState }) => {
  const { documentation = '' } = state;

  const handleUpdate = useCallback(
    ({ editor }) => {
      const md = editor.storage.markdown.getMarkdown();
      setState((prev) => ({
        ...prev,
        documentation: md,
      }));
    },
    [setState]
  );

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link.configure({ openOnClick: false }),
      Table.configure({ resizable: true }),
      TableRow,
      TableCell,
      TableHeader,
      Markdown.configure({
        html: false, // Only allow markdown, no HTML
        transformPastedText: true, // Convert pasted markdown to rich text
      }),
    ],
    content: documentation || '',
    onUpdate: handleUpdate,
  });

  useEffect(() => {
    if (editor && documentation !== undefined) {
      const currentContent = (editor.storage as any).markdown.getMarkdown();
      if (currentContent !== documentation) {
        editor.commands.setContent(documentation || '');
      }
    }
  }, [documentation, editor]);

  return (
    <Row>
      <Col lg="12">
        <div className="tiptap-editor-wrapper">
          <MenuBar editor={editor} />
          <EditorContent editor={editor} className="tiptap-content" />
        </div>
      </Col>
    </Row>
  );
};

export default Documentation;
